import { Triangle, Vector3 } from "three";
const EPSILON = 1e-8;
const triangle = new Triangle();
const startPoint = new Vector3();
const endPoint = new Vector3();
const entryPoint = new Vector3();
const startWeights = new Vector3();
const endWeights = new Vector3();
const weights = new Vector3();
const slideTarget = new Vector3();
const blockedNodes = new WeakSet();
const queuedNodes = [];
const queuedProgress = [];
// Group-local node IDs are dense. Generation stamps avoid allocating a Set or
// clearing the entire group on each step; rebuilt groups get fresh state.
const traversalStates = new WeakMap();
const traceResult = { edgeA: -1, edgeB: -1 };
function beginTraversal(nodes) {
    let state = traversalStates.get(nodes);
    if (!state || state.visited.length !== nodes.length) {
        state = { visited: new Uint32Array(nodes.length), generation: 0 };
        traversalStates.set(nodes, state);
    }
    if (state.generation === 0xffffffff) {
        state.visited.fill(0);
        state.generation = 0;
    }
    state.generation++;
    return state;
}
function setTriangle(node, vertices) {
    triangle.a.copy(vertices[node.vertexIds[0]]).setY(0);
    triangle.b.copy(vertices[node.vertexIds[1]]).setY(0);
    triangle.c.copy(vertices[node.vertexIds[2]]).setY(0);
}
function getHorizontalBarycoord(point, node, vertices, target) {
    const a = vertices[node.vertexIds[0]];
    const b = vertices[node.vertexIds[1]];
    const c = vertices[node.vertexIds[2]];
    const v0x = c.x - a.x;
    const v0z = c.z - a.z;
    const v1x = b.x - a.x;
    const v1z = b.z - a.z;
    const v2x = point.x - a.x;
    const v2z = point.z - a.z;
    const dot00 = v0x * v0x + v0z * v0z;
    const dot01 = v0x * v1x + v0z * v1z;
    const dot02 = v0x * v2x + v0z * v2z;
    const dot11 = v1x * v1x + v1z * v1z;
    const dot12 = v1x * v2x + v1z * v2z;
    const denominator = dot00 * dot11 - dot01 * dot01;
    if (denominator === 0) {
        target.set(0, 0, 0);
        return null;
    }
    const inverseDenominator = 1 / denominator;
    const u = (dot11 * dot02 - dot01 * dot12) * inverseDenominator;
    const v = (dot00 * dot12 - dot01 * dot02) * inverseDenominator;
    return target.set(1 - u - v, v, u);
}
function hasHorizontalArea(node, vertices) {
    const a = vertices[node.vertexIds[0]];
    const b = vertices[node.vertexIds[1]];
    const c = vertices[node.vertexIds[2]];
    return (b.x - a.x) * (c.z - a.z) !== (b.z - a.z) * (c.x - a.x);
}
function setHeight(position, node, vertices) {
    triangle.getBarycoord(position, weights);
    position.y = weights.x * vertices[node.vertexIds[0]].y + weights.y * vertices[node.vertexIds[1]].y + weights.z * vertices[node.vertexIds[2]].y;
}
function traceStep(start, end, node, vertices, nodes, target) {
    const { visited, generation } = beginTraversal(nodes);
    let queueLength = 1;
    queuedNodes[0] = node.id;
    queuedProgress[0] = 0;
    let furthest = -1;
    let result = node;
    traceResult.edgeA = -1;
    traceResult.edgeB = -1;
    let slideDistance = Infinity;
    while (queueLength > 0) {
        queueLength--;
        const current = nodes[queuedNodes[queueLength]];
        const entryProgress = queuedProgress[queueLength];
        if (visited[current.id] === generation)
            continue;
        setTriangle(current, vertices);
        entryPoint.lerpVectors(start, end, entryProgress).setY(0);
        // Vertical/degenerate faces are not walkable in the horizontal movement plane.
        if (!triangle.getBarycoord(entryPoint, startWeights) || !triangle.getBarycoord(end, endWeights))
            continue;
        if (Math.min(startWeights.x, startWeights.y, startWeights.z) < -EPSILON)
            continue;
        visited[current.id] = generation;
        let fraction = 1;
        for (let i = 0; i < 3; i++) {
            if (endWeights.getComponent(i) >= -EPSILON)
                continue;
            const from = Math.max(0, startWeights.getComponent(i));
            fraction = Math.min(fraction, from / (from - endWeights.getComponent(i)));
        }
        const progress = entryProgress + (1 - entryProgress) * fraction;
        if (progress > furthest) {
            furthest = progress;
            result = current;
            traceResult.edgeA = -1;
            traceResult.edgeB = -1;
            slideDistance = Infinity;
            target.lerpVectors(start, end, progress).setY(0);
            setHeight(target, current, vertices);
        }
        if (fraction === 1)
            return result;
        for (let i = 0; i < 3; i++) {
            if (endWeights.getComponent(i) >= -EPSILON)
                continue;
            const from = Math.max(0, startWeights.getComponent(i));
            const crossing = from / (from - endWeights.getComponent(i));
            if (Math.abs(crossing - fraction) > EPSILON)
                continue;
            const aId = current.vertexIds[(i + 1) % 3];
            const bId = current.vertexIds[(i + 2) % 3];
            let hasNeighbour = false;
            for (const neighbourId of current.neighbours) {
                const neighbour = nodes[neighbourId];
                if (!neighbour.vertexIds.includes(aId) || !neighbour.vertexIds.includes(bId))
                    continue;
                if (!hasHorizontalArea(neighbour, vertices))
                    continue;
                hasNeighbour = true;
                queuedNodes[queueLength] = neighbour.id;
                queuedProgress[queueLength] = progress;
                queueLength++;
            }
            if (hasNeighbour || progress < furthest)
                continue;
            const a = vertices[aId];
            const b = vertices[bId];
            projectAlongEdge(target, end, a, b, slideTarget);
            const distance = Math.pow((slideTarget.x - end.x), 2) + Math.pow((slideTarget.z - end.z), 2);
            if (distance >= slideDistance)
                continue;
            slideDistance = distance;
            result = current;
            traceResult.edgeA = aId;
            traceResult.edgeB = bId;
            target.lerpVectors(start, end, progress).setY(0);
            setHeight(target, current, vertices);
        }
    }
    return result;
}
function projectAlongEdge(start, end, a, b, target) {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const amount = ((end.x - start.x) * dx + (end.z - start.z) * dz) / (dx * dx + dz * dz);
    target.set(start.x + amount * dx, 0, start.z + amount * dz);
}
function findWalkableStart(start, node, vertices, nodes) {
    const { visited, generation } = beginTraversal(nodes);
    let queueLength = 1;
    queuedNodes[0] = node.id;
    visited[node.id] = generation;
    let closest;
    let closestDistance = Infinity;
    while (queueLength > 0) {
        const current = nodes[queuedNodes[--queueLength]];
        setTriangle(current, vertices);
        if (!triangle.getBarycoord(start, weights)) {
            for (const neighbourId of current.neighbours) {
                if (visited[neighbourId] === generation)
                    continue;
                visited[neighbourId] = generation;
                queuedNodes[queueLength++] = neighbourId;
            }
            continue;
        }
        entryPoint.copy(start).setY(0);
        triangle.closestPointToPoint(entryPoint, entryPoint);
        setHeight(entryPoint, current, vertices);
        const distance = entryPoint.distanceToSquared(start);
        if (distance < closestDistance) {
            closestDistance = distance;
            closest = current;
        }
    }
    return closest;
}
export function clampNavMeshStep(start, end, node, vertices, nodes, target) {
    startPoint.copy(start).setY(0);
    endPoint.copy(end).setY(0);
    if (!getHorizontalBarycoord(startPoint, node, vertices, weights)) {
        // Initial nearest-node lookup can select a wall. Recover through its
        // neighbours using the start position, not a destination beyond a gap.
        const walkable = findWalkableStart(start, node, vertices, nodes);
        if (!walkable) {
            if (!blockedNodes.has(node)) {
                console.warn("Navmesh movement blocked: no walkable face adjacent to the current node", node.id);
                blockedNodes.add(node);
            }
            target.copy(start);
            return node;
        }
        node = walkable;
    }
    // A triangle is convex: a destination on the current face cannot cross a gap.
    getHorizontalBarycoord(endPoint, node, vertices, weights);
    if (Math.min(weights.x, weights.y, weights.z) >= 0) {
        target.copy(endPoint);
        target.y = weights.x * vertices[node.vertexIds[0]].y + weights.y * vertices[node.vertexIds[1]].y + weights.z * vertices[node.vertexIds[2]].y;
        return node;
    }
    setTriangle(node, vertices);
    // VR tracking can remain outside the navmesh after a blocked movement.
    // Anchor the next step to the current face, never to a face beyond the gap.
    triangle.closestPointToPoint(startPoint, startPoint);
    target.copy(startPoint);
    setHeight(target, node, vertices);
    const result = traceStep(startPoint, endPoint, node, vertices, nodes, target);
    if (traceResult.edgeA < 0)
        return result;
    projectAlongEdge(target, endPoint, vertices[traceResult.edgeA], vertices[traceResult.edgeB], endPoint);
    startPoint.copy(target).setY(0);
    return traceStep(startPoint, endPoint, result, vertices, nodes, target);
}
